/* @jsx h */
import {
  computed,
  defineAsyncComponent,
  defineComponent,
  h,
  ref,
  watch,
} from "vue";
import { useQuery } from "@tanstack/vue-query";
import {
  DateRangePicker,
  ListMultiSelect,
  SegmentSelect,
  getDateRange,
  useLocalization,
  type DateRange,
  type DateRangeOption,
  type SegmentOption,
} from "@lemmonhq/uikit";
import { formatPriceInLocale } from "@lemmonhq/orders";
import {
  eachDayOfInterval,
  eachMonthOfInterval,
  eachQuarterOfInterval,
  eachWeekOfInterval,
  eachYearOfInterval,
  format,
  parseISO,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
} from "date-fns";
import type { GroupRevenueDatum } from "@lemmonhq/types";
import {
  PAYMENT_METHODS,
  getDefaultResolutionFromDateRange,
} from "@app/vue/pages/Reports/subpages/Overview/components/RevenueBars";
import { Loading } from "@app/vue/components/UIComponents";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import { useGroupAuth } from "@app/vue/providers/GroupAuth";
import { reportingShops } from "../../../reportingShops";
import { GroupRevenueBreakdownTable } from "./GroupRevenueBreakdownTable";

const GroupRevenueChart = defineAsyncComponent(() =>
  import("./GroupRevenueChart").then((module) => module.GroupRevenueChart),
);

// Distinct, theme-neutral colors assigned to restaurants by position. The same
// color is reused for the chart segment and the breakdown table so the two
// read as one control.
const RESTAURANT_PALETTE = [
  "#3b82f6",
  "#f97316",
  "#10b981",
  "#a855f7",
  "#ef4444",
  "#14b8a6",
  "#eab308",
  "#ec4899",
  "#6366f1",
  "#84cc16",
  "#f43f5e",
  "#06b6d4",
];

const restaurantColor = (index: number) =>
  RESTAURANT_PALETTE[index % RESTAURANT_PALETTE.length];

const SERVICE_METHODS = [
  { key: "dine-in", labelKey: "reports.revenue.dine_in" },
  { key: "takeaway", labelKey: "reports.revenue.takeaway" },
  { key: "delivery", labelKey: "reports.revenue.delivery" },
] as const;

type GroupBy = "day" | "week" | "month" | "quarter" | "year";

// SegmentSelect multi-select where an empty selection means "everything". The
// component hands back `undefined` when the last chip is cleared.
const toSelection = (value: string | string[] | undefined): string[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const buildDateRangeOptions = (
  t: (key: string) => string,
): DateRangeOption[] => [
  { value: "this_week", label: t("reports.revenue.this_week") },
  { value: "this_month", label: t("reports.revenue.this_month") },
  { value: "this_quarter", label: t("reports.revenue.this_quarter") },
  { value: "this_year", label: t("reports.revenue.this_year") },
  { value: "last_week", label: t("reports.revenue.last_week") },
  { value: "last_month", label: t("reports.revenue.last_month") },
  { value: "last_quarter", label: t("reports.revenue.last_quarter") },
  { value: "last_year", label: t("reports.revenue.last_year") },
  { value: "last_12m", label: t("reports.revenue.last_12_months") },
  { value: "custom", label: t("reports.revenue.custom_range") },
];

const parseRevenueDate = (value: string) =>
  parseISO(value.length > 10 ? value : `${value}T00:00:00`);

const CURRENCY = "CHF";

export const GroupRevenueDashboard = defineComponent({
  name: "GroupRevenueDashboard",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const { session } = useGroupAuth();

    const dateRangeOptions = computed(() => buildDateRangeOptions(t));
    const initialRange = getDateRange("this_month");
    const selectedRange = ref<DateRange>({
      type: "this_month",
      startDate: initialRange.startDate,
      endDate: initialRange.endDate,
    });
    const printableRange = computed(() => {
      const range = selectedRange.value;
      const start = new Date(range.startDate).toLocaleDateString();
      const end = new Date(range.endDate).toLocaleDateString();

      if (range.type === "custom") {
        return `${start} – ${end}`;
      }

      const preset = dateRangeOptions.value.find(
        (option) => option.value === range.type,
      );
      return preset
        ? `${preset.label} (${start} – ${end})`
        : `${start} – ${end}`;
    });

    // Empty selection == all. Restaurant ids empty == all restaurants.
    const selectedRestaurantIds = ref<number[]>([]);
    const selectedMethods = ref<string[]>([]);
    const selectedServiceMethods = ref<string[]>([]);
    const proportionalView = ref(false);
    const manualGroupBy = ref<GroupBy | null>(null);

    const shopsQuery = useQuery({
      queryKey: ["group", "shops"],
      queryFn: () => trpc.shops.list.query(),
    });

    // Only restaurants where the current identity has manager access (or every
    // restaurant, for a viewer) are shown and aggregated.
    const shops = computed(() =>
      reportingShops(shopsQuery.data.value ?? [], session.value.group_role),
    );
    const restaurantItems = computed(() =>
      shops.value.map((shop) => ({ id: shop.id, name: shop.name })),
    );

    // Pre-select every manager restaurant once they load so the selector shows
    // all restaurants selected on first paint.
    const selectionInitialized = ref(false);
    watch(
      shops,
      (list) => {
        if (!selectionInitialized.value && list.length > 0) {
          selectedRestaurantIds.value = list.map((shop) => shop.id);
          selectionInitialized.value = true;
        }
      },
      { immediate: true },
    );
    const shopColorById = computed(() => {
      const map = new Map<number, string>();
      shops.value.forEach((shop, index) => {
        map.set(shop.id, restaurantColor(index));
      });
      return map;
    });

    const activeRestaurantIds = computed<number[]>(() => {
      const allIds = shops.value.map((shop) => shop.id);
      if (selectedRestaurantIds.value.length === 0) return allIds;
      const selected = new Set(selectedRestaurantIds.value);
      return allIds.filter((id) => selected.has(id));
    });

    const paymentSet = new Set<string>(PAYMENT_METHODS.map((m) => m.key));
    const serviceSet = new Set<string>(SERVICE_METHODS.map((m) => m.key));

    const activeMethods = computed<string[]>(() =>
      selectedMethods.value.length === 0
        ? PAYMENT_METHODS.map((m) => m.key)
        : selectedMethods.value.filter((key) => paymentSet.has(key)),
    );
    const activeServiceMethods = computed<string[]>(() =>
      selectedServiceMethods.value.length === 0
        ? SERVICE_METHODS.map((m) => m.key)
        : selectedServiceMethods.value.filter((key) => serviceSet.has(key)),
    );

    // Payment methods drive a ListMultiSelect. Its API is id-based, so map each
    // method to its index and translate back to keys on change. Empty selection
    // means "all", matching activeMethods above.
    const paymentItems = computed(() =>
      PAYMENT_METHODS.map((m, index) => ({ id: index, name: t(m.labelKey) })),
    );
    const selectedPaymentIds = computed<number[]>(() =>
      selectedMethods.value
        .map((key) => PAYMENT_METHODS.findIndex((m) => m.key === key))
        .filter((index) => index >= 0),
    );
    const onPaymentSelectionChange = (ids: number[]) => {
      selectedMethods.value = ids.flatMap((index) => {
        const key = PAYMENT_METHODS[index]?.key;
        return key ? [key] : [];
      });
    };
    const serviceOptions = computed<SegmentOption<string>[]>(() =>
      SERVICE_METHODS.map((m) => ({ value: m.key, label: t(m.labelKey) })),
    );

    const autoGroupBy = computed<GroupBy>(() =>
      getDefaultResolutionFromDateRange(
        selectedRange.value.startDate,
        selectedRange.value.endDate,
      ),
    );
    const groupBy = computed<GroupBy>(
      () => manualGroupBy.value ?? autoGroupBy.value,
    );

    const revenueQuery = useQuery({
      queryKey: computed(() => [
        "group",
        "revenue",
        selectedRange.value.startDate,
        selectedRange.value.endDate,
      ]),
      queryFn: () =>
        trpc.groupReports.revenue.query({
          start_date_input: selectedRange.value.startDate,
          end_date_input: selectedRange.value.endDate,
        }),
    });

    const revenueData = computed<GroupRevenueDatum[]>(
      () => revenueQuery.data.value ?? [],
    );
    const loading = computed(
      () => revenueQuery.isFetching.value || shopsQuery.isFetching.value,
    );

    const bucketDates = computed<Date[]>(() => {
      const interval = {
        start: parseISO(selectedRange.value.startDate),
        end: parseISO(selectedRange.value.endDate),
      };
      switch (groupBy.value) {
        case "year":
          return eachYearOfInterval(interval);
        case "quarter":
          return eachQuarterOfInterval(interval);
        case "month":
          return eachMonthOfInterval(interval);
        case "week":
          return eachWeekOfInterval(interval, { weekStartsOn: 1 });
        default:
          return eachDayOfInterval(interval);
      }
    });

    const bucketKey = (date: Date) => {
      switch (groupBy.value) {
        case "year":
          return format(startOfYear(date), "yyyy-01-01");
        case "quarter":
          return format(startOfQuarter(date), "yyyy-MM-dd");
        case "week":
          return format(startOfWeek(date, { weekStartsOn: 1 }), "yyyy-MM-dd");
        case "month":
          return format(startOfMonth(date), "yyyy-MM-01");
        default:
          return format(date, "yyyy-MM-dd");
      }
    };

    const categories = computed(() =>
      bucketDates.value.map((date) => {
        switch (groupBy.value) {
          case "month":
            return format(date, "MMM yyyy");
          case "quarter":
            return format(date, "'Q'Q yyyy");
          case "year":
            return format(date, "yyyy");
          default:
            return format(date, "MMM d");
        }
      }),
    );

    // Per-restaurant series aligned to the bucket dates, after applying the
    // payment- and service-method filters client side.
    const series = computed(() => {
      const dates = bucketDates.value;
      const bucketIndex = new Map<string, number>();
      dates.forEach((date, index) => bucketIndex.set(bucketKey(date), index));

      const activeIds = activeRestaurantIds.value;
      const methodFilter = new Set(activeMethods.value);
      const serviceFilter = new Set(activeServiceMethods.value);
      const filterMethods = selectedMethods.value.length > 0;
      const filterServices = selectedServiceMethods.value.length > 0;

      const dataById = new Map<number, number[]>();
      activeIds.forEach((id) => dataById.set(id, Array(dates.length).fill(0)));

      revenueData.value.forEach((row) => {
        if (!dataById.has(row.restaurant_id)) return;

        const paymentKey = row.payment_method?.toLowerCase();
        if (!paymentKey || !paymentSet.has(paymentKey)) return;
        if (filterMethods && !methodFilter.has(paymentKey)) return;

        const serviceKey = (row.service_method || "dine-in").toLowerCase();
        if (filterServices && !serviceFilter.has(serviceKey)) return;

        const index = bucketIndex.get(
          bucketKey(parseRevenueDate(row.revenue_date)),
        );
        if (index === undefined) return;

        const value = Number(row.revenue) || 0;
        if (!Number.isFinite(value)) return;
        dataById.get(row.restaurant_id)![index] += value;
      });

      return shops.value
        .filter((shop) => dataById.has(shop.id))
        .map((shop) => ({
          key: String(shop.id),
          name: shop.name,
          color: shopColorById.value.get(shop.id) ?? restaurantColor(0),
          data: dataById.get(shop.id)!,
        }));
    });

    const totalSeries = computed(() => {
      const length = bucketDates.value.length;
      const result = Array(length).fill(0);
      series.value.forEach((serie) => {
        serie.data.forEach((value, index) => {
          result[index] += value;
        });
      });
      return result;
    });

    const totalRevenue = computed(() =>
      totalSeries.value.reduce((acc, value) => acc + value, 0),
    );
    const averageRevenue = computed(() => {
      const length = totalSeries.value.length;
      return length ? totalRevenue.value / length : 0;
    });
    const isEmpty = computed(
      () =>
        series.value.length === 0 ||
        totalSeries.value.every((value) => value === 0),
    );

    const money = (value: number) =>
      formatPriceInLocale(value, true, 0, CURRENCY);

    // Reset manual resolution to auto whenever the range changes.
    watch(
      () => [selectedRange.value.startDate, selectedRange.value.endDate],
      () => {
        manualGroupBy.value = null;
      },
    );

    const legendItems = computed(() =>
      series.value.map((serie) => ({
        key: serie.key,
        label: serie.name,
        color: serie.color,
      })),
    );

    const renderChart = () => (
      <div class="relative min-h-[360px]">
        {loading.value ? (
          <div class="absolute inset-0 flex items-center justify-center bg-base-100/60 backdrop-blur-sm">
            <Loading />
          </div>
        ) : isEmpty.value ? (
          <div class="absolute inset-0 flex flex-col items-center justify-center gap-2 text-base-content/60">
            <span class="material-symbols-outlined text-5xl opacity-40">
              insights
            </span>
            <p class="px-4 text-center text-sm">
              {t("admin.dashboard.no_data")}
            </p>
          </div>
        ) : (
          <GroupRevenueChart
            dates={bucketDates.value}
            categories={categories.value}
            series={series.value}
            totalSeries={totalSeries.value}
            loading={loading.value}
            groupBy={groupBy.value}
            proportionalView={proportionalView.value}
            currency={CURRENCY}
          />
        )}
      </div>
    );

    return () => (
      <div class="flex min-h-0 w-full flex-1 flex-col overflow-y-auto overflow-x-hidden">
        {/* Sticky filter header */}
        <div class="sticky top-0 z-[1] glossy-bg border-b px-4 py-4 backdrop-blur">
          <div class="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
            <div class="flex flex-shrink-0 flex-col gap-4 sm:flex-row sm:items-end">
              <div class="min-w-[200px] space-y-2">
                <label class="flex items-center gap-2 text-sm font-semibold text-base-content/80">
                  <span class="material-symbols-outlined text-3xl text-primary">
                    date_range
                  </span>
                  {t("reports.revenue.date_range")}
                </label>
                <DateRangePicker
                  value={selectedRange.value}
                  options={dateRangeOptions.value}
                  onChange={(range) => {
                    if (range) selectedRange.value = range;
                  }}
                  className="w-full sm:w-[180px]"
                />
              </div>
              <div class="min-w-[200px] space-y-2">
                <label class="flex items-center gap-2 text-sm font-semibold text-base-content/80">
                  <span class="material-symbols-outlined text-3xl text-primary">
                    storefront
                  </span>
                  {t("admin.dashboard.restaurants")}
                </label>
                <ListMultiSelect
                  items={restaurantItems.value}
                  selectedIds={selectedRestaurantIds.value}
                  onChange={(ids) => {
                    selectedRestaurantIds.value = ids;
                  }}
                  loading={shopsQuery.isPending.value}
                  buttonLabel={t("admin.dashboard.restaurants")}
                  searchPlaceholder={t("admin.dashboard.search_restaurants")}
                  compact={true}
                  widthClass="w-full sm:w-auto"
                />
              </div>
            </div>

            <div class="flex flex-1 flex-col gap-4 sm:flex-row sm:items-end sm:justify-end">
              <div class="min-w-[200px] space-y-2">
                <label class="flex items-center gap-2 text-sm font-semibold text-base-content/80">
                  <span class="material-symbols-outlined text-3xl text-primary">
                    payment
                  </span>
                  {t("reports.revenue.payment_method")}
                </label>
                <ListMultiSelect
                  items={paymentItems.value}
                  selectedIds={selectedPaymentIds.value}
                  onChange={onPaymentSelectionChange}
                  buttonLabel={t("reports.revenue.payment_method")}
                  searchPlaceholder={t(
                    "reports.revenue.search_payment_methods",
                  )}
                  compact={true}
                  widthClass="w-full"
                />
              </div>
              <div class="min-w-[200px] space-y-2">
                <label class="flex items-center gap-2 text-sm font-semibold text-base-content/80">
                  <span class="material-symbols-outlined text-3xl text-primary">
                    room_service
                  </span>
                  {t("reports.revenue.service_method")}
                </label>
                <SegmentSelect
                  options={serviceOptions.value}
                  value={selectedServiceMethods.value}
                  allowMultiple={true}
                  onChange={(value) => {
                    selectedServiceMethods.value = toSelection(value);
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div class="flex flex-col gap-6 p-4">
          <div class="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div class="space-y-2">
              <h2 class="flex items-center gap-3 text-3xl font-semibold text-accent">
                {t("reports.revenue.revenue_analytics")}
              </h2>
              <p class="max-w-3xl text-sm leading-relaxed text-base-content/70">
                <span class="hidden text-base-content/60 sm:inline">
                  {printableRange.value}
                </span>
              </p>
            </div>

            <div class="grid max-w-full gap-4 sm:grid-cols-2 xl:max-w-[700px] xl:flex-1">
              <div class="rounded-2xl border bg-base-100 p-3 shadow-lg transition hover:shadow-xl">
                <div class="flex items-center justify-between">
                  <div class="flex flex-1 items-center gap-3">
                    <span class="inline-flex items-center justify-center rounded-xl border border-success/30 bg-success/10 p-2 text-success">
                      <span class="material-symbols-outlined text-lg">
                        trending_up
                      </span>
                    </span>
                    <span class="text-sm font-semibold uppercase tracking-wide text-base-content/70">
                      {t("reports.revenue.total_revenue")}
                    </span>
                  </div>
                  <div class="flex-1 text-right text-2xl font-bold text-accent">
                    {money(totalRevenue.value)}
                  </div>
                </div>
              </div>
              <div class="rounded-2xl border bg-base-100 p-3 shadow-lg transition hover:shadow-xl">
                <div class="flex items-center gap-3">
                  <span class="inline-flex items-center justify-center rounded-xl border border-info/30 bg-info/10 p-2 text-info">
                    <span class="material-symbols-outlined text-lg">
                      calculate
                    </span>
                  </span>
                  <span class="text-sm font-semibold uppercase tracking-wide text-base-content/70">
                    {t("reports.revenue.average")}
                  </span>
                  <div class="flex-1 text-right text-2xl font-bold text-accent">
                    {money(averageRevenue.value)}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="rounded-2xl border  bg-base-100 p-4 shadow-lg">
            <div class="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div class="flex flex-wrap items-center gap-3 text-xs text-base-content/70 sm:text-sm">
                {legendItems.value.length > 0 ? (
                  legendItems.value.map((item) => (
                    <div key={item.key} class="flex items-center gap-2">
                      <span
                        class="inline-flex h-3 w-3 rounded-full border /50"
                        style={{ background: item.color }}
                      />
                      <span>{item.label}</span>
                    </div>
                  ))
                ) : (
                  <span class="italic text-base-content/50">
                    {t("admin.dashboard.no_restaurants_selected")}
                  </span>
                )}
              </div>
              <div class="flex items-center gap-2">
                <select
                  class="select select-sm select-bordered min-w-[120px] bg-base-100"
                  value={groupBy.value}
                  onChange={(e) => {
                    manualGroupBy.value = (e.target as HTMLSelectElement)
                      .value as GroupBy;
                  }}
                >
                  <option value="day">{t("reports.revenue.period_day")}</option>
                  <option value="week">
                    {t("reports.revenue.period_week")}
                  </option>
                  <option value="month">
                    {t("reports.revenue.period_month")}
                  </option>
                  <option value="quarter">
                    {t("reports.revenue.period_quarter")}
                  </option>
                  <option value="year">
                    {t("reports.revenue.period_year")}
                  </option>
                </select>
                <button
                  class="btn btn-sm btn-ghost gap-1"
                  onClick={() => {
                    proportionalView.value = !proportionalView.value;
                  }}
                >
                  <span class="material-symbols-outlined text-base">
                    {proportionalView.value ? "payments" : "percent"}
                  </span>
                  <span class="text-xs">
                    {proportionalView.value
                      ? t("reports.revenue.absolute")
                      : t("reports.revenue.proportion")}
                  </span>
                </button>
              </div>
            </div>
            {renderChart()}
          </div>

          <GroupRevenueBreakdownTable
            periods={bucketDates.value}
            resolution={groupBy.value}
            series={series.value}
            totals={totalSeries.value}
            currency={CURRENCY}
            loading={loading.value}
          />
        </div>
      </div>
    );
  },
});
